import fetchClient from "./axiosInstance";
import { ActiveOrg, AuthResponse, Location, UserQuotas } from "@/schema";
import { User } from "@/schema";
import { activeContextStore } from "./ActiveContextStore";
import { readSessionSnapshot, replaceSessionSlot } from "./sessionSnapshot";

/**
 * Service for handling authentication with the backend.
 * Uses JWT Bearer token authentication.
 *
 * STATE OWNER (sweep §4): the ONE atomic session snapshot. This service stays
 * the door CALLER; its fields hydrate FROM the snapshot at construction (a
 * reload renders instantly on the persisted bearer — the token rides AS-IS
 * with its real expiry, the window NOT shortened) and every refresh/clear
 * REPLACES the snapshot's auth slot atomically. The old memory-only mirror
 * (full blocking re-auth on every reload) is dead; the background refresh
 * reconciles the same door.
 */
export class AuthenticateService {
  private safeRefreshMs: number = 5 * 60 * 1000;
  private currentToken: string | null = null;
  private expiresAt: number | null = null;
  private refreshInFlight: Promise<void> | null = null;
  private listeners = new Set<
    (state: { token: string | null; expiresAt: number | null }) => void
  >();
  // Cached user information returned from the server
  private currentUser: User | null = null;
  private currentUserQuotas: UserQuotas | null = null;
  private currentLocation: Location | null = null;
  private currentActiveOrg: ActiveOrg | null = null;
  private currentActiveUser: User | null = null;

  constructor() {
    // hydrate from the session snapshot: a still-valid bearer + its identity
    // render the app instantly; the scheduled/background refresh reconciles.
    const auth = readSessionSnapshot().auth;
    if (auth && typeof auth.token === "string" && auth.token
      && Number.isFinite(Number(auth.expiresAt)) && Number(auth.expiresAt) > Date.now()) {
      this.currentToken = auth.token;
      this.expiresAt = Number(auth.expiresAt);
      this.currentUser = (auth.user as User) ?? null;
      this.currentActiveUser = (auth.activeUser as User) ?? null;
      this.currentActiveOrg = (auth.activeOrg as ActiveOrg) ?? null;
      this.currentUserQuotas = (auth.userQuotas as UserQuotas) ?? null;
      this.currentLocation = (auth.location as Location) ?? null;
    }
  }

  /** Replace the snapshot's auth slot atomically (the ONE persisted record). */
  private persistAuth(): void {
    if (!this.currentToken || !this.expiresAt) {
      replaceSessionSlot("auth", null);
      return;
    }
    replaceSessionSlot("auth", {
      token: this.currentToken,
      expiresAt: this.expiresAt,
      user: this.currentUser,
      activeUser: this.currentActiveUser,
      activeOrg: this.currentActiveOrg,
      userQuotas: this.currentUserQuotas,
      location: this.currentLocation,
    });
  }

  private normalizeBearer(token: string): string {
    const t = String(token || "").trim();
    if (!t) return "";
    // Accept either "<jwt>" or "Bearer <jwt>" and normalize to "Bearer <jwt>".
    if (/^bearer\s+/i.test(t)) {
      return `Bearer ${t.replace(/^bearer\s+/i, "").trim()}`;
    }
    return `Bearer ${t}`;
  }

  private notifyListeners(): void {
    const state = { token: this.currentToken, expiresAt: this.expiresAt };
    for (const listener of this.listeners) {
      listener(state);
    }
  }

  /**
   * Subscribe to auth state changes (token refresh / logout).
   * Returns an unsubscribe function.
   */
  public subscribe(
    listener: (state: { token: string | null; expiresAt: number | null }) => void
  ): () => void {
    this.listeners.add(listener);
    // Immediately provide current state for convenience.
    listener({ token: this.currentToken, expiresAt: this.expiresAt });
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Refresh authentication using stored session or provided session ID
   * @returns Promise that resolves when refresh is complete
   */
  public async refresh(providerToken?: string): Promise<void> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          ...(providerToken
            ? { Authorization: this.normalizeBearer(providerToken) }
            : {}),
        };

        const selectedContext = activeContextStore.read();
        const payload = selectedContext
          ? {
              selectedUserId: selectedContext.user.id,
              selectedOrgId: selectedContext.org.id,
            }
          : null;
        const { data: authData } = await fetchClient.post<AuthResponse>(
          `/authenticate`,
          payload,
          { headers }
        );

        // Update current session data
        this.currentToken = this.normalizeBearer(authData.Authorization);
        this.currentUser = authData.user;
        this.currentUserQuotas = authData.userQuotas;
        this.expiresAt = new Date(authData.expiresAt).getTime();
        this.currentLocation = authData.location;
        this.currentActiveOrg = authData.activeContext.org;
        this.currentActiveUser = authData.activeContext.user;
        if (!this.currentActiveUser?.id || !this.currentActiveOrg?.id) {
          throw new Error("Authentication returned no active user/org context");
        }
        if (selectedContext && (
          this.currentActiveUser.id !== selectedContext.user.id ||
          this.currentActiveOrg.id !== selectedContext.org.id
        )) {
          throw new Error("Authentication returned a different active context");
        }
        const hydrated = activeContextStore.hydrate(authData.activeContext);
        this.currentActiveOrg = hydrated.org;
        this.persistAuth();
        this.notifyListeners();
      } catch (error) {
        this.clearAuth();
        throw error;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /**
   * Get the current session ID
   * @returns The current session ID or null if no session exists
   */
  public getToken(): string {
    if (!this.currentToken || !this.isAuthenticated()) {
      throw new Error("Authentication expired");
    }

    return this.currentToken;
  }

  /**
   * Get headers needed for authenticated requests
   * @returns Headers object with current session ID and Bearer token
   */
  public getAuthHeaders(customAuth?: string): Record<string, string> {
    return {
      ...(customAuth
        ? { Authorization: `Bearer ${customAuth}` }
        : { Authorization: this.getToken() }),
    };
  }

  /**
   * Check if there's an active authenticated session
   * @returns boolean indicating if there's an active session
   */
  public isAuthenticated(): boolean {
    const hasToken = !!this.currentToken;
    const hasExpiration = !!this.expiresAt;
    const isNotExpired = this.getExpiresAtToken() >= Date.now();

    return !!(hasToken && hasExpiration && isNotExpired);
  }

  /**
   * Get the authenticated user information (if available)
   */
  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  public updateCurrentUser(user: User): void {
    this.currentUser = user;
    this.persistAuth();
  }

  public getUserQuotas(): UserQuotas | null {
    return this.currentUserQuotas;
  }

  public getCurrentLocation(): Location | null {
    return this.currentLocation;
  }

  public getActiveOrg(): ActiveOrg | null {
    return this.currentActiveOrg;
  }

  public getActiveUser(): User | null {
    return this.currentActiveUser;
  }

  public getExpiresAtToken(): number {
    return (this.expiresAt || Date.now()) - this.safeRefreshMs;
  }

  /**
   * Clear the current session
   */
  public clearAuth(): void {
    this.currentToken = null;
    this.currentUserQuotas = null;
    this.currentUser = null;
    this.currentLocation = null;
    this.currentActiveOrg = null;
    this.currentActiveUser = null;
    this.expiresAt = null;
    this.persistAuth();
    this.notifyListeners();
  }

  // ─── Guest Token ──────────────────────────────────────────────────────────

  private guestAgentId: string | null = null;
  private guestSessionId: string | null = null;
  private guestVersionKey: string | null = null;

  /**
   * Guest preview is disabled until it is owned by the outlet/channel path.
   * Stores a session ID in localStorage so returning visitors get the same session.
   */
  public async getGuestToken(params?: {
    company?: string;
    industry?: string;
    role?: string;
    goal?: string;
    ref?: string;
  }): Promise<{ token: string; sessionId: string; agentId: string; expiresAt: number }> {
    let sessionId = localStorage.getItem('causit_guest_session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem('causit_guest_session_id', sessionId);
    }

    // Guest preview is not wired to the channel/outlet yet. Until then there
    // is no guest token.
    void sessionId;
    void params;
    throw new Error("Guest preview is not available (pending outlet wiring)");
  }

  public getGuestAgentId(): string | null {
    return this.guestAgentId;
  }

  public getGuestSessionId(): string | null {
    return this.guestSessionId;
  }

  public getGuestVersionKey(): string | null {
    return this.guestVersionKey;
  }
}

export const authenticateService = new AuthenticateService();
